import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import moment from 'moment';
import { BAlert } from 'bootstrap-vue';
import { Contact } from '@contacts/interfaces/contact';
import { SubscriptionCategory } from '@subscriptions/interfaces/subscription-category';
import SubscriptionConfirmationModal from '@subscriptions/components/modals/subscription-confirmation-modal/subscription-confirmation-modal.vue';
import SubscriptionConfirmedModal from '@subscriptions/components/modals/subscription-confirmed-modal/subscription-confirmed-modal.vue';
import AddSubscriptionToShoppingCartModal from '@subscriptions/components/modals/add-subscription-to-shopping-cart-modal/add-subscription-to-shopping-cart-modal.vue';
import _ from 'lodash';
import { Subscription } from '@subscriptions/interfaces/subscription';

library.add(faSync);

@Component({
  name: 'bpc-subscription-category-details',
  components: {
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-subscription-confirmation-modal': SubscriptionConfirmationModal,
    'bpc-subscription-confirmed-modal': SubscriptionConfirmedModal,
    'bpc-subscription-to-shopping-cart-modal': AddSubscriptionToShoppingCartModal,
    'b-alert': BAlert,
  },
})
export default class SubscriptionCategoryDetails extends Vue {
  $refs: {
    buySubscriptionModal: InstanceType<typeof AddSubscriptionToShoppingCartModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    subscriptionConfirmationModal: InstanceType<typeof SubscriptionConfirmationModal> & {
      onOpenModal(): void;
      onCloseModal(): void;
    };
    subscriptionConfirmedModal: InstanceType<typeof SubscriptionConfirmedModal> & {
      onOpenModal(): void;
      onCloseModal(): void;
    };
  };
  @Prop(String) readonly title: string;
  @Prop(String) readonly subtitle: string;
  @Prop({ type: [String, Number], required: true }) readonly entityId: string | number;

  public loading: boolean = false;
  public contactsToBuySubscription: number[] = [];
  public confirmedSubscriptions: Subscription[] = [];
  public subscriptionCategory: SubscriptionCategory = null;
  public entityObservableSubscription = null;
  public contactObservableSubscription = null;
  public contactRelationsObservableSubscription = null;
  public successText: string = 'global.generic.successful_action';
  public errorText: string = 'global.generic.error_form_action';
  public contacts: Contact[] = [];
  public contactLoaded = false;
  public contactRelationsLoaded = false;

  public created(): void {
    if (this.entityId) {
      this.getEntity();
      this.getContact();
      this.getContactRelations();
    }
  }

  public getEntity(): void {
    this.loading = true;
    const select = {
      columns: [
        'id',
        'code',
        'name',
        'status',
        'article_id',
        'short_description',
        'description',
        'type',
        'number_credits',
        'start_date_type',
        'end_date_type',
        'duration_type',
        'fixed_start_date',
        'fixed_end_date',
        'fixed_duration_number',
        'published',
        'publish_start_date',
        'publish_end_date',
        'publish_start_date_time',
        'publish_end_date_time',
        'allowed_limit',
      ],
      relationFields: ['disciplines'],
      computedFields: ['remaining_subscriptions'],
      withRelations: ['disciplines'],
    };
    const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'subscription-category',
      'raw',
    )}/${this.entityId}`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(fetchEntityPath, {
      params: select,
    });

    if (this.entityObservableSubscription) {
      this.entityObservableSubscription.unsubscribe();
    }

    this.entityObservableSubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.subscriptionCategory = res.data;
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  public getContact(): void {
    this.contactLoaded = false;

    const contactId = this.$authorizationService.getContactId();
    const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'contact',
      'raw',
    )}/${contactId}`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(fetchEntityPath);

    if (this.contactObservableSubscription) {
      this.contactObservableSubscription.unsubscribe();
    }

    this.contactObservableSubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.contactLoaded = true;
        this.contacts.push(res.data);
      },
      error => {
        console.log(error);
      },
    );
  }

  public getContactRelations(): void {
    this.contactRelationsLoaded = false;
    const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('contact-relations', 'raw')}`;

    const queryConfig = {
      params: {
        contact_id: this.$authorizationService.getContactId(),
      },
    };
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(
      fetchEntityPath,
      queryConfig,
    );

    if (this.contactRelationsObservableSubscription) {
      this.contactRelationsObservableSubscription.unsubscribe();
    }

    this.contactRelationsObservableSubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        if (res.data) {
          this.contactRelationsLoaded = true;
          res.data.forEach(contactRelation => {
            this.contacts.push(contactRelation['relatedContact']);
          });
        }
      },
      error => {
        console.log(error);
      },
    );
  }

  get hasPermissionToBuy(): boolean {
    return this.$authorizationService.hasPermissionForAction('subscriptions', 'buy', true);
  }

  get shoppingCartActivated(): boolean {
    return this.$store.getters['centerSettings/shoppingCartActivated'];
  }

  public getRemainingSubscriptions(): void {
    const select = {
      columns: ['id', 'allowed_limit'],
      relationFields: [],
      computedFields: ['remaining_subscriptions'],
      withRelations: [],
    };
    const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'subscription-category',
      'raw',
    )}/${this.entityId}`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(fetchEntityPath, {
      params: select,
    });

    if (this.entityObservableSubscription) {
      this.entityObservableSubscription.unsubscribe();
    }

    this.entityObservableSubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.subscriptionCategory.remaining_subscriptions = res.data?.remaining_subscriptions;
      },
      error => {
        console.log(error);
      },
    );
  }

  get remainingSubscriptions() {
    return this.subscriptionCategory?.remaining_subscriptions;
  }

  get publishStartDate() {
    return this.subscriptionCategory?.publish_start_date;
  }

  get publishStartDateTime() {
    return this.subscriptionCategory?.publish_start_date_time;
  }

  get publishEndDate() {
    return this.subscriptionCategory?.publish_end_date;
  }

  get publishEndDateTime() {
    return this.subscriptionCategory?.publish_end_date_time;
  }

  get availableToBuy() {
    return (
      _.isNull(this.subscriptionCategory.remaining_subscriptions) ||
      this.subscriptionCategory.remaining_subscriptions > 0
    );
  }

  get isUnlimited() {
    return _.isNull(this.subscriptionCategory.remaining_subscriptions);
  }

  checkPublishStartDateTime() {
    const today = moment();
    let result = true;
    if (this.publishStartDate) {
      const startDateTime = moment(this.publishStartDate);

      if (this.publishStartDateTime) {
        const data = this.publishStartDateTime.split(':');
        startDateTime.hour(parseInt(data[0])).minute(parseInt(data[1]));
      } else {
        startDateTime.endOf('day');
      }

      if (today.isSameOrBefore(startDateTime)) {
        result = false;
      }
    }
    return result;
  }

  checkPublishEndDateTime() {
    const today = moment();
    let result = true;
    if (this.publishEndDate) {
      const startDateTime = moment(this.publishEndDate);

      if (this.publishEndDateTime) {
        const data = this.publishEndDateTime.split(':');
        startDateTime.hour(parseInt(data[0])).minute(parseInt(data[1]));
      } else {
        startDateTime.endOf('day');
      }

      if (today.isSameOrAfter(startDateTime)) {
        result = false;
      }
    }
    return result;
  }

  isPublished(): boolean {
    return this.checkPublishStartDateTime() && this.checkPublishEndDateTime();
  }

  subscriptionStartDate() {
    return this.subscriptionCategory.start_date_type === 'fixed_date'
      ? this.subscriptionCategory.fixed_start_date
      : moment(new Date()).format('YYYY-MM-DD');
  }

  subscriptionEndDate() {
    return this.subscriptionCategory.end_date_type === 'fixed_date'
      ? this.subscriptionCategory.fixed_end_date
      : moment(this.subscriptionStartDate())
          .add(
            this.subscriptionCategory.fixed_duration_number,
            this.subscriptionCategory.duration_type,
          )
          .format('YYYY-MM-DD');
  }

  showBuySubscriptionModal() {
    this.$refs.buySubscriptionModal.onOpenModal(this.subscriptionCategory.id);
    this.contactsToBuySubscription = [];
  }

  onSuccessSubscriptionCreation(subscriptions: Subscription[]) {
    this.confirmedSubscriptions = subscriptions;
    this.$refs.buySubscriptionModal.onCloseModal();
    if (this.shoppingCartActivated) {
      this.$refs.subscriptionConfirmationModal.onOpenModal();
    } else {
      this.$refs.subscriptionConfirmedModal.onOpenModal();
    }
    this.$root.$emit('refresh-orders');
  }

  onCloseConfirmationModal($event) {
    this.getRemainingSubscriptions();
  }

  public formatDate(date): string {
    if (date) {
      return moment(String(date)).format('DD/MM/YYYY');
    }
  }
}
